import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Eye, Clock, MapPin, MessageCircle, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {AutoTranslate} from "@/lib/autoTranslate.tsx";
import { useTranslation} from "react-i18next";

interface ResumeAnalyticsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sharedResumeId: string;
  resumeTitle: string;
}

interface AnalyticsData {
  totalViews: number;
  avgTimeSpent: number;
  topLocations: { country: string; count: number }[];
  commentCount: number;
  viewsThisWeek: number;
}

export function ResumeAnalyticsDialog({
  open,
  onOpenChange,
  sharedResumeId,
  resumeTitle
}: ResumeAnalyticsDialogProps) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);

  useEffect(() => {
    if (open && sharedResumeId) {
      loadAnalytics();
    }
  }, [open, sharedResumeId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Get all views for this shared resume
      const { data: views, error: viewsError } = await supabase
        .from("resume_views")
        .select("*")
        .eq("shared_resume_id", sharedResumeId);

      if (viewsError) throw viewsError;

      // Get comment count
      const { count: commentCount, error: commentError } = await supabase
        .from("resume_comments")
        .select("*", { count: "exact", head: true })
        .eq("shared_resume_id", sharedResumeId);

      if (commentError) throw commentError;

      // Calculate analytics
      const totalViews = views?.length || 0;

      // Calculate average time spent
      const validTimes = views?.filter(v => v.time_spent_seconds > 0) || [];
      const avgTimeSpent = validTimes.length > 0
        ? Math.round(validTimes.reduce((acc, v) => acc + v.time_spent_seconds, 0) / validTimes.length)
        : 0;

      // Get views this week
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const viewsThisWeek = views?.filter(v => new Date(v.created_at) >= oneWeekAgo).length || 0;

      // Get top locations
      const locationMap = new Map<string, number>();
      views?.forEach(v => {
        if (v.viewer_country) {
          locationMap.set(v.viewer_country, (locationMap.get(v.viewer_country) || 0) + 1);
        }
      });
      const topLocations = Array.from(locationMap.entries())
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setAnalytics({
        totalViews,
        avgTimeSpent,
        topLocations,
        commentCount: commentCount || 0,
        viewsThisWeek
      });
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };
  const transText = useTranslation().t('autoTranslate', { returnObjects: true });
  const { i18n } = useTranslation();


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Resume Analytics
          </DialogTitle>
          <DialogDescription>
            Performance insights for "{resumeTitle}"
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : analytics ? (
          <div className="space-y-4">
            {/* Weekly summary banner */}
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-lg font-semibold text-foreground">
                {AutoTranslate(`This resume was viewed`)} {analytics.viewsThisWeek} {AutoTranslate(`time`)}{analytics.viewsThisWeek !== 1 ? 's' : ''} this week
              </p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Eye className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{analytics.totalViews}</p>
                      <p className="text-xs text-muted-foreground">{AutoTranslate(`Total Views`)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{formatTime(analytics.avgTimeSpent)}</p>
                      <p className="text-xs text-muted-foreground">{AutoTranslate(`Avg. Time Spent`)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <MessageCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{analytics.commentCount}</p>
                      <p className="text-xs text-muted-foreground">{AutoTranslate(`Comments`)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {analytics.totalViews > 0
                          ? Math.round((analytics.commentCount / analytics.totalViews) * 100)
                          : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">{AutoTranslate(`Engagement Rate`)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Geographic data */}
            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-primary" />
                  <p className="font-medium">{AutoTranslate(`Top Viewer Locations`)}</p>
                </div>
                {analytics.topLocations.length > 0 ? (
                  <div className="space-y-2">
                    {analytics.topLocations.map((loc, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{loc.country}</span>
                        <span className="font-medium">{loc.count} {AutoTranslate(`view`)}{loc.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{AutoTranslate(`No location data available yet`)}</p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            {AutoTranslate(`Unable to load analytics data`)}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}